import { CHARACTER_LIMITS, HANDS, LOCAL_STORAGE_KEYS, PASSCODE_RESPONSES } from '@/constants';
import { ENV } from '@/environment';
import jwt from 'jsonwebtoken';
import { sortBy } from 'lodash';
import store from '../store';
import { OVERLAY_SIZE } from '../constants';

export default {
  matchText({ text, query }) {
    return query.length === 0 || text?.toLowerCase().includes(query?.toLowerCase());
  },
  isKlaviyoAvailable() {
    return typeof _learnq !== 'undefined' && typeof window._learnq.isIdentified === 'function';
  },
  postGoogleEvent({ event, ...rest }) {
    // eslint-disable-next-line
    dataLayer?.push({
      event,
      ...rest,
    });

    // eslint-disable-next-line
    gtag('event', event, rest);
  },
  encryptUrlSafe(str) {
    return encodeURIComponent(window.btoa(str));
  },
  decryptUrlSafe(str) {
    return decodeURIComponent(window.atob(str));
  },
  getJsonFromLocalStorage({ key }) {
    return JSON.parse(localStorage.getItem(key) ?? '{}');
  },
  setJsonInLocalStorage({ key, json }) {
    localStorage.setItem(key, JSON.stringify(json));
  },
  getCompletedSteps() {
    return this.getJsonFromLocalStorage({ key: LOCAL_STORAGE_KEYS.COMPLETED_STEPS });
  },
  setCompletedStep(stepId) {
    this.setJsonInLocalStorage({
      key: LOCAL_STORAGE_KEYS.COMPLETED_STEPS,
      json: {
        ...this.getCompletedSteps(),
        [stepId]: true,
      },
    });
  },
  isStepCompleted(step) {
    return this.getCompletedSteps()[step.id];
  },
  getLastPlayedVideo() {
    return this.getJsonFromLocalStorage({ key: LOCAL_STORAGE_KEYS.LAST_QUERY });
  },
  isLoggedIn({ slug } = {}) {
    const storedPasscodes = this.getWooJwt()[slug] || [];
    return storedPasscodes.length > 0;
  },
  validateTutorialPassword: ({ id, code, sku }) => {
    const body = { id, code, kit: id, sku };

    return fetch(`${ENV.API_ROOT}/api/verify`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(r => r.json())
      .then(r => ({
        ...r,
        id,
        code,
        message: PASSCODE_RESPONSES[r.message],
      }))
      .catch(() => ({
        message: 'Oops... something went wrong. Please try again later.',
      }));

    // ============================================================
    // Verify remotely: when dev mode is enabled only
    // ============================================================
    // if (store.state[LOCAL_STORAGE_KEYS.REMOTE_CODE_VERIFICATION]) {
    //   const body = { id, code, kit: id, sku };

    //   return fetch(`${ENV.API_ROOT}/api/verify`, {
    //     method: 'POST',
    //     body: JSON.stringify(body),
    //     headers: {
    //       'Content-Type': 'application/json',
    //     },
    //   })
    //     .then(r => r.json())
    //     .then(r => ({
    //       ...r,
    //       id,
    //       code,
    //       message: PASSCODE_RESPONSES[r.message],
    //     }))
    //     .catch(() => ({
    //       message: 'Oops... something went wrong. Please try again later.',
    //     }));
    // }
    // // ============================================================

    // // Client-side verification
    // return new Promise(resolve => {
    //   const result = tutorialPasscodes.find(t => code.trim().toLowerCase() === atob(t.code).trim().toLowerCase());

    //   if (result) {
    //     resolve({
    //       ...result,
    //       valid: true,
    //     });
    //   } else {
    //     resolve({
    //       valid: false,
    //     });
    //   }
    // });
  },
  getWooJwt: () => {
    return jwt.decode(localStorage.getItem(LOCAL_STORAGE_KEYS.WOO_JWT)) || {};
  },
  setWooJwt: jwtObject => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.WOO_JWT, jwt.sign(jwtObject, 'woo'));
  },
  unique: arr => arr.filter((v, i, a) => a.indexOf(v) === i),
  getVideoOptions: ({ microstep, tutorialId, videoOptions }) => {
    const hand = store.state.hands[tutorialId];

    let flip = false;
    let video = microstep.videos.find(v => !v.lefty);

    // If the user is left-handed
    if (hand === HANDS.LEFT) {
      // Check if we have an override video and use it if found
      const leftyVideo = microstep.videos.find(v => v.lefty);

      if (leftyVideo) {
        video = leftyVideo;
      } else {
        flip = true;
      }
    }

    let overlays = microstep.overlays;
    let tracks = (video.subtitles ?? []).map(t => ({
      src: ENV.S3_ROOT + t.url,
      label: t.label,
      language: t.lang,
      mode: store.state.subtitlesOn ? 'showing' : 'hidden',
    }));

    // For debugging
    if (ENV.DEV) {
      overlays = [
        {
          text: 'Reading basic patterns',
          size: 'Large',
          start_time: 0,
          end_time: 200,
        },
        {
          text: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a here is text that exceeds the threshold too long',
          start_time: 0,
          end_time: 200,
          size: 'Small',
          text_alignment: 'left',
        },
        {
          text: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a here is text that exceeds the threshold too long',
          start_time: 0,
          end_time: 200,
          text_alignment: 'left',
        },
        {
          text: 'One line link',
          url: 'https://www.google.com',
          start_time: 0,
          end_time: 200,
        },
      ].map((o, id) => ({ ...o, id }));
      tracks = [
        {
          src: 'https://woobles-staging-preview.s3.amazonaws.com/videos/microsteps/pierre-the-penguin-the-wings-all-done--all-done--2.vtt',
          label: 'English',
          language: 'en',
          mode: store.state.subtitlesOn ? 'showing' : 'hidden',
        },
      ];
    }

    overlays = overlays.map(o => ({
      ...o,
      size: o.size || OVERLAY_SIZE.M,
    }));

    return {
      ...videoOptions,
      addBackgroundMusic: video.add_background_music,
      backgroundMusicSrc: video.background_music_url || `${ENV.S3_ROOT}/bkgd.mp3`,
      flip,
      sources: [
        {
          ...video,
          src: ENV.S3_ROOT + video.url,
          type: 'video/mp4',
        },
      ],
      tracks,
      overlays: sortBy(overlays, ['size', 'start_time'], ['asc', 'asc']).map(o => {
        const adjusted = { ...o };

        // Clip text that exceeds the thresholds
        if (o.size === 'Large' && !o.url) {
          // Large text
          adjusted.text = adjusted.text?.slice(0, CHARACTER_LIMITS.LARGE_TEXT);
        } else if (!o.url) {
          // Small Text
          adjusted.text = adjusted.text?.slice(0, CHARACTER_LIMITS.SMALL_TEXT);
        }

        return adjusted;
      }),
    };
  },
  isMobile: () => {
    if (window.innerWidth <= 600) return true;

    if (typeof navigator === 'undefined') return false;

    let check = false;
    (function checkIfMobile(a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
          a,
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
          a.substr(0, 4),
        )
      )
        check = true;
    })(navigator.userAgent || navigator.vendor || window.opera);
    return check;
  },
  makeid: length => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  },
};
